import { isEmpty } from "lodash";
import { useMemo, useState } from "react";

import { useCachedTag } from "src/hooks/useCachedTag";
import { useCachedGroup } from "src/hooks/useSearchGroups";
import { useCachedPerson } from "src/hooks/useSearchPeople";
import useUpdateEffect from "src/hooks/useUpdateEffect";
import { getUpdatedAttributes } from "src/components/TagsEditor/utils/getUpdatedAttributes";
import { ATTRIBUTE_ROW_TYPE } from "src/pages/Workspaces/consts";
import { isTagDisplayValueEditable } from "../TagsEditor.util";
import { inNameAttributes } from "src/types/tagConfigAttributes";
import {
  buildDisplayValue,
  filterTagAttributeTypes,
} from "./TagAttributeModal.logic";
import {
  attributeStoreForField,
  nextAttributeMap,
} from "./TagAttributesModal.fieldUpdates";

const EmptyAttrTypes = [];

export const useTagAttributeStores = ({
  entityTag,
  tagTypeConfig,
  entityId,
}) => {
  const [displayValue, setDisplayValue] = useState(entityTag.displayValue);
  const [updatedAttributes, setUpdatedAttributes] = useState(
    getUpdatedAttributes({
      tagType: tagTypeConfig,
      entityId,
      attributes: entityTag.attributes || {},
      typeKeys: [ATTRIBUTE_ROW_TYPE.others, ATTRIBUTE_ROW_TYPE.values],
    })
  );
  const [updatedEntityTagAttributes, setUpdatedEntityTagAttributes] = useState(
    entityTag.entityTagAttributes || {}
  );
  const [requiredFieldsMessage, setRequiredFieldsMessage] = useState(null);

  useUpdateEffect(() => {
    setUpdatedEntityTagAttributes(entityTag.entityTagAttributes || {});
  }, [entityTag.id, entityTag.entityTagAttributes]);

  return {
    displayValue,
    setDisplayValue,
    updatedAttributes,
    setUpdatedAttributes,
    updatedEntityTagAttributes,
    setUpdatedEntityTagAttributes,
    requiredFieldsMessage,
    setRequiredFieldsMessage,
  };
};

export const useTagAttributeMeta = (tagTypeConfig) => {
  const {
    name: tagTypeName,
    hasDetails,
    detailsParsed: parsedDetails,
    attributes: meta = {},
  } = tagTypeConfig || {};

  const {
    separator = "_",
    values: nameAttrTypes = EmptyAttrTypes,
    others: otherAttrTypes = EmptyAttrTypes,
    entityTag: entityTagAttrTypes = EmptyAttrTypes,
    parentTag = EmptyAttrTypes,
  } = meta || {};

  const [readonlyAttrTypes, editableAttrTypes, uniqueAttrTypes] = useMemo(
    () =>
      filterTagAttributeTypes(
        nameAttrTypes,
        otherAttrTypes,
        entityTagAttrTypes
      ),
    [nameAttrTypes, otherAttrTypes, entityTagAttrTypes]
  );

  return {
    tagTypeName,
    hasDetails,
    parsedDetails,
    parentTag,
    separator,
    nameAttrTypes,
    isDisplayValueEditable: isTagDisplayValueEditable(
      inNameAttributes({ values: nameAttrTypes }),
      tagTypeConfig
    ),
    readonlyAttrTypes,
    editableAttrTypes,
    uniqueAttrTypes,
  };
};

export const useTagDisplayValueSync = ({
  nameAttrTypes,
  updatedAttributes,
  separator,
  entityId,
  setDisplayValue,
}) => {
  const { getCachedGroup } = useCachedGroup();
  const { getCachedPerson } = useCachedPerson();
  const { getCachedTag } = useCachedTag();

  useUpdateEffect(() => {
    if (isEmpty(nameAttrTypes)) {
      return;
    }
    setDisplayValue(
      buildDisplayValue({
        attrTypeValues: nameAttrTypes,
        attributes: updatedAttributes,
        separator,
        getCachedGroup: getCachedGroup as (
          id: unknown
        ) => { name?: string } | null | undefined,
        getCachedPerson,
        getCachedTag: getCachedTag as (
          id: unknown
        ) => { displayValue?: string } | null | undefined,
        entityId,
      })
    );
  }, [
    nameAttrTypes,
    separator,
    updatedAttributes,
    getCachedGroup,
    getCachedPerson,
    getCachedTag,
    entityId,
    setDisplayValue,
  ]);
};

export const applyAttributePatch = ({
  id,
  value,
  isEntityTagAttr,
  updatedAttributes,
  updatedEntityTagAttributes,
  setUpdatedAttributes,
  setUpdatedEntityTagAttributes,
  onFieldUpdate,
  clearFieldToBeFocused,
  setRequiredFieldsMessage,
}) => {
  const targetAttributes = attributeStoreForField(
    isEntityTagAttr,
    updatedAttributes,
    updatedEntityTagAttributes
  );
  const updateMethod = isEntityTagAttr
    ? setUpdatedEntityTagAttributes
    : setUpdatedAttributes;
  const newAttributes = nextAttributeMap(targetAttributes, id, value);
  updateMethod(newAttributes);
  onFieldUpdate(newAttributes);
  clearFieldToBeFocused();
  setRequiredFieldsMessage(null);
};
